import React from 'react';
import axios from "axios";
import {jwtDecode} from 'jwt-decode';

import Property from "./Property";

class Utils extends React.Component {
  /** @type {Promise<string | null> | null} */
  static refreshPromise = null;
  static interceptorInstalled = false;
  static SESSION_EVENT = "session-change";

  static showMessage(componentObject, error) {
    let message = error.message;
    if (typeof error.response !== 'undefined' && typeof error.response.data !== 'undefined') {
      message = error.response.status + ":" + (error.response.data.message ?? error.response.data.msg);
    }
    if (componentObject) {
      componentObject.setState({message: message});
      componentObject.state['message'] = message;
    }
  }

  static setupAxiosInterceptors() {
    if (this.interceptorInstalled) {
      return;
    }
    this.interceptorInstalled = true;
    axios.interceptors.request.use(async (config) => {
      if (config.url && config.url.includes("/api/refresh")) {
        return config;
      }
      const token = await Utils.ensureFreshAccessToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = "Bearer " + token;
      }
      return config;
    });
  }

  static generateAuthorizationHeaderValue() {
    return "Bearer " + this.getAccessToken();
  }

  static ensureFreshAccessToken(componentObject) {
    let accessToken = this.getAccessToken();
    let refreshToken = this.getRefreshToken();
    if (!accessToken && !refreshToken) {
      return Promise.resolve(null);
    }
    if (accessToken && !this.isTokenExpired(accessToken)) {
      return Promise.resolve(accessToken);
    }
    if (!refreshToken || this.isTokenExpired(refreshToken)) {
      this.setAccessToken(null);
      this.setRefreshToken(null);
      return Promise.resolve(null);
    }
    if (!this.refreshPromise) {
      this.refreshPromise = Promise.resolve(this.refreshAccessToken(componentObject))
        .finally(() => {
          this.refreshPromise = null;
        });
    }
    return this.refreshPromise;
  }

  static getStored(name) {
    return localStorage.getItem(name);
  }

  static setStored(name, value) {
    if (!value) {
      localStorage.removeItem(name);
      return null;
    }
    localStorage.setItem(name, value);
    return value;
  }

  static getAccessToken() {
    return this.getStored("access-token");
  }

  static setAccessToken(accessToken) {
    const stored = this.setStored("access-token", accessToken);
    this.notifySessionChange();
    return stored;
  }

  static getRefreshToken() {
    return this.getStored("refresh-token");
  }

  static setRefreshToken(refreshToken) {
    const stored = this.setStored("refresh-token", refreshToken);
    this.notifySessionChange();
    return stored;
  }

  static notifySessionChange() {
    window.dispatchEvent(new Event(this.SESSION_EVENT));
  }

  static getUsername() {
    return this.getStored("username");
  }

  static setUsername(username) {
    return this.setStored("username", username);
  }

  static logout() {
    this.setAccessToken(null);
    this.setRefreshToken(null);
    this.setUsername(null);
  }

  static isLogged() {
    let accessToken = this.getAccessToken();
    let refreshToken = this.getRefreshToken();
    return (!!accessToken && !this.isTokenExpired(accessToken))
      || (!!refreshToken && !this.isTokenExpired(refreshToken));
  }

  static redirectToLogin() {
    window.location.replace("/login?next=" + encodeURIComponent(window.location.pathname));
  }

  static getTokenExpirationDate(token) {
    try {
      let decoded = typeof token === "string" ? jwtDecode(token) : token;
      return decoded && decoded.exp ? new Date(decoded.exp * 1000) : null;
    } catch (exception) {
      return null;
    }
  }

  static deleteToken(token) {
    if (!token || !token.id) {
      return Promise.reject(new Error('No token id provided'));
    }

    const url = `${Property.BASE_API_URL}/api/tokens/${token.id}`;

    return axios.delete(url, {
      headers: {
        'Authorization': this.generateAuthorizationHeaderValue(),
        'Content-Type': 'application/json',
        'Access-Control-Allow-Methods': 'DELETE'
      }
    }).then(() => {
      if (token.type === 'access_token' && this.hasJti(this.getAccessToken(), token.jti)) {
        this.setAccessToken(null);
      } else if (token.type === 'refresh_token' && this.hasJti(this.getRefreshToken(), token.jti)) {
        this.setRefreshToken(null);
      }
      return token;
    });
  }

  static hasJti(rawToken, jti) {
    if (!rawToken || !jti) {
      return false;
    }
    try {
      return jwtDecode(rawToken).jti === jti;
    } catch (exception) {
      return false;
    }
  }

  static isTokenExpired(token) {
    let expirationDate = this.getTokenExpirationDate(token);
    return !expirationDate || expirationDate.getTime() < new Date().getTime();
  }

  static isSessionToken(token) {
    if (token.type === 'access_token') {
      return this.hasJti(this.getAccessToken(), token.jti);
    }
    return token.type === 'refresh_token' && this.hasJti(this.getRefreshToken(), token.jti);
  }

  //todo: introduce a decorator for post or get methods and call this method inside if needed
  static refreshAccessToken(componentObject) {
    let site = Property.BASE_API_URL;
    let updateTokenUrl = site + "/api/refresh";
    let refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      if (componentObject) {
        this.showMessage(componentObject, 'No refresh token exist.');
      }
      return null;
    }
    let updateConfig = {
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Methods': 'POST',
        'Access-Control-Allow-Origin': site,
        'Authorization': "Bearer " + refreshToken
      }
    };

    return axios.post(updateTokenUrl, null, updateConfig)
      .then((response) => {
        console.log("update token response: ", response);
        Utils.setAccessToken(response.data.access_token);
        return response.data.access_token;
      }).catch((error) => {
        console.log(error);
        if (error.response == null) {
          console.log('404 ', error);
        } else if (error.response.status === 401) {
          console.log('401 ', error);
        }
        if (error.response && [401, 422].includes(error.response.status)) {
          this.setAccessToken(null);
          this.setRefreshToken(null);
        }
        this.showMessage(componentObject, error);
      });
  }
}

export default Utils